import type { TerritoryBoundary } from '@user/store/territory/typings'
import type {
  ConditionModel,
  LeadAssignmentRuleFormModel,
} from './LeadAssignmentRuleForm/typings'

/**
 * One entry in a condition's value list. Only the id is read here -- the name travels with it
 * so the chip keeps its label -- so this names the two fields rather than the form's full model.
 */
type ConditionValue = { id?: string; name?: string }

/**
 * A territory drawn for a rule but not yet created (WWM-17508).
 *
 * It sits in the condition exactly where a real territory would, so the multi-select renders it
 * as another chip, validation counts it as a value, and nothing downstream needs to know it is
 * special -- until the rule is saved, when it is swapped for the id the server gives back.
 *
 * The sentinel is a string because every other condition value is: territory ids are stored and
 * returned as strings, and a number here would not survive the round trip.
 */
export const DRAFT_TERRITORY_ID = '__drafted-territory__'

export type DraftedTerritoryValue = {
  id: typeof DRAFT_TERRITORY_ID
  name: string
  boundary: TerritoryBoundary
}

const isTerritoryCondition = (condition: ConditionModel) =>
  condition?.attribute === 'Territory'

/**
 * Rebuilds the parked rule form, with the newly drawn territory folded into its Territory
 * condition.
 *
 * `drafted` null means the admin went to the map and came back empty-handed. The form is
 * restored exactly as they left it: giving up on a drawing is not giving up on the rule.
 */
export function resumeRuleWithDraftedTerritory(
  parkedRule: Record<string, unknown>,
  parkedCondition: Record<string, unknown> | null,
  drafted: { name: string; boundary: TerritoryBoundary } | null,
): LeadAssignmentRuleFormModel {
  const rule = parkedRule as unknown as LeadAssignmentRuleFormModel
  const condition = parkedCondition as unknown as ConditionModel | null

  // No drafted shape covers two different trips back: cancelling the drawing, and finishing a
  // REDRAW -- finishTerritoryRedraw leaves `drafted` null because a redraw edits a territory that
  // already exists rather than producing a new unsaved one. Either way the parked condition still
  // has to come back: the condition popup keeps its own Formik state, so territories picked there
  // immediately before leaving for the map live only in parkedCondition, and returning the rule
  // alone silently dropped them when the parent form remounted.
  if (!drafted) {
    return condition && isTerritoryCondition(condition)
      ? mergeTerritoryCondition(rule, condition)
      : rule
  }

  // The condition they were editing when they left, or a fresh one if they had not got that far.
  const base: ConditionModel =
    condition && isTerritoryCondition(condition)
      ? condition
      : {
          attribute: 'Territory',
          matchType: 'Any',
          assignmentRuleConditionValues: [],
        }

  const draftedValue: DraftedTerritoryValue = {
    id: DRAFT_TERRITORY_ID,
    name: drafted.name,
    boundary: drafted.boundary,
  }

  // Any previous draft is dropped rather than accumulated: a second trip to the map replaces the
  // shape, it does not add a second unsaved territory to the same condition.
  const withDrafted: ConditionModel = {
    ...base,
    assignmentRuleConditionValues: [
      ...(base.assignmentRuleConditionValues ?? []).filter(
        (value: ConditionValue) => value?.id !== DRAFT_TERRITORY_ID,
      ),
      draftedValue,
    ],
  }

  return mergeTerritoryCondition(rule, withDrafted)
}

/**
 * Puts a Territory condition back into the rule, replacing the one already there.
 *
 * Replace rather than append: the attribute dropdown allows only one condition per attribute, so
 * two would be unreachable by any other route and would make the rule impossible to edit
 * afterwards.
 */
function mergeTerritoryCondition(
  rule: LeadAssignmentRuleFormModel,
  condition: ConditionModel,
): LeadAssignmentRuleFormModel {
  const conditions = rule.assignmentRuleConditions ?? []
  const existing = conditions.findIndex(isTerritoryCondition)

  const merged =
    existing >= 0
      ? conditions.map((c, index) => (index === existing ? condition : c))
      : [...conditions, condition]

  return { ...rule, assignmentRuleConditions: merged }
}

/** The unsaved territory sitting in these conditions, if there is one. */
export function findDraftedTerritory(
  conditions: ConditionModel[] | undefined,
): DraftedTerritoryValue | null {
  for (const condition of conditions ?? []) {
    if (!isTerritoryCondition(condition)) continue

    const drafted = (condition.assignmentRuleConditionValues ?? []).find(
      (value: ConditionValue) => value?.id === DRAFT_TERRITORY_ID,
    )

    if (drafted) return drafted as DraftedTerritoryValue
  }

  return null
}

/**
 * Swaps the unsaved territory for the one the server created, so the rule is saved against a
 * real id. Called after the territory POST succeeds and before the rule is sent.
 */
export function replaceDraftedTerritory(
  conditions: ConditionModel[],
  createdId: number,
): ConditionModel[] {
  return conditions.map((condition) =>
    isTerritoryCondition(condition)
      ? {
          ...condition,
          assignmentRuleConditionValues: (
            condition.assignmentRuleConditionValues ?? []
          ).map((value: ConditionValue) =>
            value?.id === DRAFT_TERRITORY_ID
              ? { id: String(createdId), name: value.name }
              : value,
          ),
        }
      : condition,
  )
}
